/**
 * "O que fazer agora" da biblioteca: sugestoes em ordem de urgencia
 * (insights.ts), cada uma com o numero que a justifica e UM botao. Mostra as
 * quatro primeiras e abre o resto em "ver mais": a lista cresce com a
 * biblioteca, e o que e urgente precisa caber sem rolar.
 *
 * O botao devolve a `AcaoInsight` para a pagina, que e quem sabe abrir o
 * editor, a previa (ja na aba certa), a comparacao ou refiltrar a lista. O
 * vazio e uma boa noticia escrita: "nada parado" tambem e informacao.
 */

import { useState } from 'react';
import { Button, Icon, Panel, SectionCard, type BadgeTone } from '../../../components/ui';
import type { AcaoInsight, InsightModelo } from '../insights';

const VISIVEIS = 4;

const TOM_DO_PAINEL: Readonly<Record<BadgeTone, 'surface' | 'brand' | 'good' | 'warning' | 'critical' | 'accent'>> = {
  neutral: 'surface',
  brand: 'brand',
  accent: 'accent',
  info: 'surface',
  good: 'good',
  warning: 'warning',
  critical: 'critical',
  violet: 'surface',
};

const TOM_DO_ICONE: Readonly<Record<BadgeTone, string>> = {
  neutral: 'bg-ink/5 text-ink-muted',
  brand: 'bg-brand/10 text-brand',
  accent: 'bg-accent/10 text-accent',
  info: 'bg-info/10 text-info',
  good: 'bg-status-good/10 text-status-good',
  warning: 'bg-status-warning/15 text-status-warning',
  critical: 'bg-status-critical/10 text-status-critical',
  violet: 'bg-series-7/10 text-series-7',
};

export function OQueFazerModelos({
  insights,
  onAcao,
  className = '',
}: {
  readonly insights: readonly InsightModelo[];
  readonly onAcao: (acao: AcaoInsight) => void;
  readonly className?: string;
}): JSX.Element {
  const [todas, setTodas] = useState(false);
  const mostradas = todas ? insights : insights.slice(0, VISIVEIS);
  const escondidas = insights.length - mostradas.length;
  return (
    <SectionCard
      id="o-que-fazer-modelos"
      title="O que fazer agora"
      icon="clique"
      description="Sugestões tiradas dos números da biblioteca: toques parados na Meta, quedas, gargalos no comercial e o que copiar do melhor."
      className={className}
    >
      {insights.length === 0 ? (
        <div className="flex flex-col gap-1 rounded-xl border border-dashed border-edge bg-elevated px-4 py-6 text-center">
          <p className="text-sm font-medium text-ink">Nada parado por aqui.</p>
          <p className="text-sm text-ink-muted">Todo toque ligado a uma régua tem modelo aprovado, nenhum pagamento caiu forte e não há diferença grande entre modelos da mesma categoria.</p>
        </div>
      ) : (
        <ol className="flex flex-col gap-3">
          {mostradas.map((insight, i) => (
            <li key={insight.id}>
              <Panel tone={TOM_DO_PAINEL[insight.tone]} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span aria-hidden="true" className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg ${TOM_DO_ICONE[insight.tone]}`}>
                    <Icon name={insight.icone} size="sm" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">
                      <span className="mr-1.5 tabular-nums text-ink-subtle">{i + 1}.</span>
                      {insight.titulo}
                    </p>
                    <p className="mt-0.5 text-sm text-ink-muted">{insight.texto}</p>
                  </div>
                </div>
                <Button variant="secondary" size="sm" onClick={() => onAcao(insight.acao)} className="shrink-0">
                  {insight.rotuloAcao}
                </Button>
              </Panel>
            </li>
          ))}
        </ol>
      )}
      {escondidas > 0 || todas ? (
        <div className="mt-3">
          <Button variant="ghost" size="sm" icon="chevron" onClick={() => setTodas((v) => !v)} aria-expanded={todas}>
            {todas ? 'Mostrar só as mais urgentes' : `Ver mais ${escondidas} ${escondidas === 1 ? 'sugestão' : 'sugestões'}`}
          </Button>
        </div>
      ) : null}
    </SectionCard>
  );
}
